import { useForm } from '@magicdoorinc/form';
import { useLocalization, createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { PayOutsRepository } from '~/repositories/payOutsRepository';
import { SpecialEntityType } from '~/utils/constant';
import type {
  AddCompanyPayoutsDto,
  BankAccountDto,
  ChartOfAccountDto,
  CompanyPayoutGroupDto,
  CompanyPayoutLineItemDto,
  GetPaginatedManagementFeesParams,
  PayoutResultsDto,
} from '~/swagger/portal/Api';

const payOutsRepo = new PayOutsRepository();

export interface MixedPayOutBankAccountDto {
  propertyBankAccount?: BankAccountDto;
  amount?: number;
  totalAmount?: number;
  paymentType?: string;
  paymentMethod?: string;
  externalTransactionId?: string;
  memo?: string;
  propertyBankAccountId?: string;
  companyBankAccountId?: string;
  companyChartOfAccountId?: string;
  propertyChartOfAccount?: ChartOfAccountDto;
  lineItems?: (CompanyPayoutLineItemDto & { paymentType?: string; paymentMethod?: string; externalTransactionId?: string; memo: string })[];
}

export const [PayOutsProvider, usePayOuts] = createMagicDoorContext('PayOuts', () => {
  const [filtered, setFilter, actions] = createTriggerResource((filter: GetPaginatedManagementFeesParams) =>
    payOutsRepo.getPayOuts(filter)
  );
  const [canNext, setCanNext] = createSignal(true);
  const { t } = useLocalization();
  const addPayOut = createMutation((payload: AddCompanyPayoutsDto) => payOutsRepo.addPayOut(payload));

  const deletePayOut = createMutation(async (payOutId: string) => {
    await payOutsRepo.deletePayOut(payOutId);
    actions.refetch();
  });

  const splitGroups = (groups: CompanyPayoutGroupDto[]) => {
    const targetTypes = [SpecialEntityType.ManagementFee, SpecialEntityType.ServiceBill];
    const targetGroupIndex = groups.findIndex((group) =>
      group.lineItems.some((item) => targetTypes.includes(item.entityType as SpecialEntityType))
    );

    if (targetGroupIndex === -1) {
      return groups as Array<CompanyPayoutGroupDto & { entityType?: SpecialEntityType.ManagementFee | SpecialEntityType.ServiceBill }>;
    }

    const targetGroup = groups[targetGroupIndex];
    const otherGroups = [...groups.slice(0, targetGroupIndex), ...groups.slice(targetGroupIndex + 1)];

    const subGroups = targetGroup.lineItems.reduce(
      (acc, item) => {
        const entityType = item.entityType as SpecialEntityType;
        if (targetTypes.includes(entityType)) {
          if (!acc[entityType]) {
            acc[entityType] = {
              entityType,
              totalAmount: 0,
              lineItems: [],
            };
          }
          acc[entityType].lineItems.push(item);
          acc[entityType].totalAmount += item.amount;
        }
        return acc;
      },
      {} as Record<SpecialEntityType, CompanyPayoutGroupDto & { entityType: SpecialEntityType }>
    );

    return [...otherGroups, ...Object.values(subGroups)] as Array<
      CompanyPayoutGroupDto & { entityType?: SpecialEntityType.ManagementFee | SpecialEntityType.ServiceBill }
    >;
  };

  const [calculatePayOuts, getCalculatePayOuts] = createTriggerResource(async () => {
    const result = await payOutsRepo.calculatePayOuts();

    return {
      ...result,
      groups: splitGroups(result.groups),
    };
  });

  const steps = createMemo(() => [t('Payouts'), t('Payment'), t('Review & print')]);

  const handlePayouts = async () => {
    const { validateStatus } = payoutsForm.validateForm();
    if (!validateStatus) throw new Error('Invalid form');
    const res = await addPayOut(payoutsForm.formStore as AddCompanyPayoutsDto);
    setResponseData(res);
  };

  const [responseData, setResponseData] = createSignal<PayoutResultsDto>();

  const [currentStep, _setCurrentStep] = createSignal<number>(0);

  const setCurrentStep = (step: number) => {
    _setCurrentStep(step);
  };

  const payoutsForm = useForm();

  return {
    filtered,
    setFilter: (filter: GetPaginatedManagementFeesParams) => setFilter(filter),
    addPayOut,
    deletePayOut,
    steps,
    canNext,
    setCanNext,
    handlePayouts,
    payoutResults: responseData,
    getCalculatePayOuts,
    calculatePayOuts,
    currentStep,
    setCurrentStep,
    payoutsForm,
  };
});
